export type TelegramWebApp = {
  initData: string;
  initDataUnsafe?: {
    user?: {
      id: number;
      first_name?: string;
      last_name?: string;
      username?: string;
      photo_url?: string;
    };
  };
  ready: () => void;
  expand: () => void;
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  enableClosingConfirmation?: () => void;
  HapticFeedback?: {
    impactOccurred: (style: "light" | "medium" | "heavy" | "rigid" | "soft") => void;
    notificationOccurred: (type: "error" | "success" | "warning") => void;
    selectionChanged: () => void;
  };
};

export function getTelegram(): TelegramWebApp | null {
  if (typeof window === "undefined") return null;
  return (window as unknown as { Telegram?: { WebApp?: TelegramWebApp } }).Telegram?.WebApp ?? null;
}

const TELEGRAM_SDK = "https://telegram.org/js/telegram-web-app.js";
let telegramLoad: Promise<TelegramWebApp | null> | null = null;

/** True only inside a Telegram Mini App webview. */
function isTelegramContext(): boolean {
  if (typeof window === "undefined") return false;
  if (getTelegram()) return true;
  const w = window as unknown as { TelegramWebviewProxy?: unknown };
  if (w.TelegramWebviewProxy) return true;
  return /tgWebApp/i.test(window.location.hash + window.location.search);
}

/**
 * Loads the Telegram SDK on demand. Ordinary web visitors never download it,
 * which keeps a third-party script off the critical path.
 */
export function ensureTelegram(): Promise<TelegramWebApp | null> {
  if (typeof window === "undefined") return Promise.resolve(null);
  const existing = getTelegram();
  if (existing) return Promise.resolve(existing);
  if (!isTelegramContext()) return Promise.resolve(null);
  if (!telegramLoad) {
    telegramLoad = new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = TELEGRAM_SDK;
      script.async = true;
      script.onload = () => resolve(getTelegram());
      script.onerror = () => resolve(null);
      document.head.appendChild(script);
    });
  }
  return telegramLoad;
}


export function initTelegram(): void {
  const tg = getTelegram();
  if (!tg) return;
  try {
    tg.ready();
    tg.expand();
    tg.setHeaderColor?.("#000000");
    tg.setBackgroundColor?.("#000000");
  } catch {
    /* Telegram host may not support every method */
  }
}

export function haptic(kind: "light" | "medium" | "heavy" = "light"): void {
  try {
    getTelegram()?.HapticFeedback?.impactOccurred(kind);
  } catch {
    /* haptics are best-effort */
  }
}

export function hapticSuccess(): void {
  try {
    getTelegram()?.HapticFeedback?.notificationOccurred("success");
  } catch {
    /* haptics are best-effort */
  }
}

export function getDeviceId(): string {
  const key = "stp_device_id";
  const existing = localStorage.getItem(key);
  if (existing) return existing;
  const id = crypto.randomUUID();
  localStorage.setItem(key, id);
  return id;
}